import { Typography } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { fabricationFiltersAdmin } from 'src/components/3d/Assemblies';
import { ButtonRow } from 'src/components/common';
import { CategoryType } from 'src/constants/category';
import useFilters from 'src/hook/useFilter';
import { Piece, updatePiece } from 'src/services/pieces';
import {
  AxisInputGroup,
  BlockedAxisSelector,
  CategorySelect,
  MaterialSelect,
  PieceDetailsForm,
} from './components';
import { ButtonContainer, Input, StyledButton } from './styles';
import { AxisInput, PieceDetails } from './types';
import { castPieceDetails } from './utils';

type UpdatePieceProps = {
  piece: Piece;
  refreshPieces: () => Promise<void>;
  setEditingId: (editingId: number | undefined) => void;
};

export const UpdatePiece = ({ piece, refreshPieces, setEditingId }: UpdatePieceProps) => {
  const [category, setCategory] = useState<CategoryType>(piece.type);
  const [material, setMaterial] = useState<'GRANIT' | 'INOX'>(piece.material || 'GRANIT');
  const [blockedAxis, setBlockedAxis] = useState<string[]>(piece.blockedAxis);
  const [name, setName] = useState<string>(piece.name);
  const [fabricationFiltersComponent, fabricationFilterState] = useFilters(
    fabricationFiltersAdmin,
    piece.pieceFabrication.map((el) => el.fabrication),
  );

  const [details, setDetails] = useState<{
    french: { linear: PieceDetails; m2: PieceDetails };
    import: { linear: PieceDetails; m2: PieceDetails };
  }>({
    french: {
      linear: {
        faceX: String(piece.frenchLinearDetails.faceX),
        faceX2: String(piece.frenchLinearDetails.faceX2),
        faceY: String(piece.frenchLinearDetails.faceY),
        faceY2: String(piece.frenchLinearDetails.faceY2),
        faceZ: String(piece.frenchLinearDetails.faceZ),
        faceZ2: String(piece.frenchLinearDetails.faceZ2),
      },
      m2: {
        faceX: String(piece.frenchM2Details.faceX),
        faceX2: String(piece.frenchM2Details.faceX2),
        faceY: String(piece.frenchM2Details.faceY),
        faceY2: String(piece.frenchM2Details.faceY2),
        faceZ: String(piece.frenchM2Details.faceZ),
        faceZ2: String(piece.frenchM2Details.faceZ2),
      },
    },
    import: {
      linear: {
        faceX: String(piece.importLinearDetails.faceX),
        faceX2: String(piece.importLinearDetails.faceX2),
        faceY: String(piece.importLinearDetails.faceY),
        faceY2: String(piece.importLinearDetails.faceY2),
        faceZ: String(piece.importLinearDetails.faceZ),
        faceZ2: String(piece.importLinearDetails.faceZ2),
      },
      m2: {
        faceX: String(piece.importM2Details.faceX),
        faceX2: String(piece.importM2Details.faceX2),
        faceY: String(piece.importM2Details.faceY),
        faceY2: String(piece.importM2Details.faceY2),
        faceZ: String(piece.importM2Details.faceZ),
        faceZ2: String(piece.importM2Details.faceZ2),
      },
    },
  });
  const [polishing, setPolishing] = useState<AxisInput>({
    x: String(piece.polishingX),
    y: String(piece.polishingY),
    z: String(piece.polishingZ),
  });
  const [sawing, setSawing] = useState<AxisInput>({
    x: String(piece.sawingX),
    y: String(piece.sawingY),
    z: String(piece.sawingZ),
  });
  const [extra, setExtra] = useState<{ french: string; import: string }>({
    french: String(piece.frenchExtra),
    import: String(piece.importExtra),
  });
  const [minPrice, setMinPrice] = useState<{ french: string; import: string }>({
    french: String(piece.minPriceFrench),
    import: String(piece.minPriceImport),
  });

  const changeDetails = (
    country: 'french' | 'import',
    mode: 'linear' | 'm2',
    key: keyof PieceDetails,
    value: string,
  ) => {
    setDetails({
      ...details,
      [country]: {
        ...details[country],
        [mode]: { ...details[country][mode], [key]: value },
      },
    });
  };

  const handleExtraChange = (country: 'french' | 'import', value: string) => {
    setExtra({ ...extra, [country]: value });
  };

  const handleMinPriceChange = (country: 'french' | 'import', value: string) => {
    setMinPrice({ ...minPrice, [country]: value });
  };

  const handleUpdate = async () => {
    try {
      await updatePiece(
        piece.id,
        name,
        category,
        material,
        fabricationFilterState,
        blockedAxis,
        {
          x: Number.parseFloat(polishing.x),
          y: Number.parseFloat(polishing.y),
          z: Number.parseFloat(polishing.z),
        },
        {
          x: Number.parseFloat(sawing.x),
          y: Number.parseFloat(sawing.y),
          z: Number.parseFloat(sawing.z),
        },
        {
          french: {
            extra: Number.parseFloat(extra.french),
            linear: castPieceDetails(details.french.linear),
            m2: castPieceDetails(details.french.m2),
            minPrice: Number.parseFloat(minPrice.french),
          },
          import: {
            extra: Number.parseFloat(extra.import),
            linear: castPieceDetails(details.import.linear),
            m2: castPieceDetails(details.import.m2),
            minPrice: Number.parseFloat(minPrice.import),
          },
        },
      );

      toast.success('Pièce mise à jour avec succès');
      await refreshPieces();
      setEditingId(undefined);
    } catch {
      console.warn('Error');
    }
  };

  return (
    <>
      <Input
        value={name}
        type="text"
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <CategorySelect
        category={category}
        setCategory={setCategory}
        style={{ marginTop: '8px', marginBottom: '8px' }}
      />
      <MaterialSelect
        material={material}
        setMaterial={setMaterial}
        style={{ marginBottom: '8px' }}
      />
      <>
        <BlockedAxisSelector blockedAxis={blockedAxis} setBlockedAxis={setBlockedAxis} />
        <AxisInputGroup label="Polissage" value={polishing} onChange={setPolishing} />
        <AxisInputGroup label="Sciage" value={sawing} onChange={setSawing} />
        <PieceDetailsForm
          details={details}
          extra={extra}
          minPrice={minPrice}
          onChangeDetails={changeDetails}
          onChangeExtra={handleExtraChange}
          onChangeMinPrice={handleMinPriceChange}
        />
      </>
      <ButtonRow>{fabricationFiltersComponent}</ButtonRow>

      <ButtonContainer>
        <StyledButton
          variant="contained"
          onClick={() => {
            handleUpdate();
          }}
        >
          <Typography variant="body1" style={{ fontSize: '12px', textTransform: 'capitalize' }}>
            Update piece
          </Typography>
        </StyledButton>
      </ButtonContainer>
    </>
  );
};
